import { useId, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

export type NewLessonType = "video" | "text" | "live_session";

/** CRS-11: "Live session" is offered only when live classes are configured. */
export function addableLessonTypes(liveClasses: boolean): NewLessonType[] {
  return liveClasses ? ["video", "text", "live_session"] : ["video", "text"];
}

type Props = {
  courseId: string;
  modules: { id: string; title: string }[];
  /** CRS-3: the module new lessons go into — the last created or picked one. */
  activeModuleId: string | null;
  onActiveModuleChange: (moduleId: string) => void;
  liveClasses: boolean;
  onCreated: () => Promise<void> | void;
};

const TYPE_LABEL_KEYS: Record<NewLessonType, string> = {
  video: "contentTypeVideo",
  text: "contentTypeText",
  live_session: "contentTypeLiveSession",
};

export function AddLessonForm({
  courseId,
  modules,
  activeModuleId,
  onActiveModuleChange,
  liveClasses,
  onCreated,
}: Props) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const [title, setTitle] = useState("");
  const [contentType, setContentType] = useState<NewLessonType>("video");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const needsModuleHintId = useId();
  const moduleSelectId = useId();
  const types = addableLessonTypes(liveClasses);
  // If live classes were switched off while "Live session" was picked, fall back to video.
  const selectedType = types.includes(contentType) ? contentType : "video";
  const moduleId =
    activeModuleId && modules.some((mod) => mod.id === activeModuleId)
      ? activeModuleId
      : (modules[0]?.id ?? null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!moduleId) return;
    setError(null);
    setSaving(true);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/modules/${moduleId}/lessons`, {
        method: "POST",
        body: JSON.stringify({ title: title.trim(), contentType: selectedType }),
      });
      if (!res.ok) {
        setError(
          apiErrorText(await readApiErrorResponse(res), tRoot, "instructor.addLessonFailed", {
            namespace: "instructor.errors",
          }),
        );
        return;
      }
      setTitle("");
      await onCreated();
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h2 className="text-lg font-medium">{t("lessonsTitle")}</h2>
      <form
        onSubmit={handleSubmit}
        className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]"
      >
        <label className="sr-only" htmlFor={moduleSelectId}>
          {t("moduleSelectLabel")}
        </label>
        <select
          id={moduleSelectId}
          data-testid="add-lesson-module"
          className="rounded border px-3 py-2 border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          value={moduleId ?? ""}
          onChange={(e) => onActiveModuleChange(e.target.value)}
          disabled={!moduleId}
          title={t("moduleSelectLabel")}
        >
          {modules.map((mod) => (
            <option key={mod.id} value={mod.id}>
              {mod.title}
            </option>
          ))}
        </select>
        <input
          data-testid="add-lesson-title"
          aria-label={t("lessonPlaceholder")}
          className="rounded border px-3 py-2 border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          placeholder={t("lessonPlaceholder")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={!moduleId}
        />
        <select
          data-testid="add-lesson-content-type"
          aria-label={t("lessonTypeLabel")}
          className="rounded border px-3 py-2 border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          value={selectedType}
          onChange={(e) => setContentType(e.target.value as NewLessonType)}
          disabled={!moduleId}
        >
          {types.map((type) => (
            <option key={type} value={type}>
              {t(TYPE_LABEL_KEYS[type])}
            </option>
          ))}
        </select>
        <button
          type="submit"
          data-testid="add-lesson-submit"
          className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)] disabled:opacity-50"
          disabled={!moduleId || saving}
          aria-describedby={moduleId ? undefined : needsModuleHintId}
        >
          {t("addLesson")}
        </button>
      </form>
      {/* A lesson needs a module to live in, so the form is disabled until one exists. */}
      {moduleId ? null : (
        <p
          id={needsModuleHintId}
          data-testid="add-lesson-needs-module"
          className="mt-2 text-sm text-[var(--text-muted)]"
        >
          {t("addLessonNeedsModule")}
        </p>
      )}
      {liveClasses ? null : (
        <p className="mt-2 text-xs text-[var(--text-muted)]" data-testid="live-classes-off-hint">
          {t("liveClassesNotConfigured")}
        </p>
      )}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-600" data-testid="add-lesson-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
